import { nativeJson, portalJson } from './lib/nativeApi.js';
import { NativeObjectInspector } from './components/NativeObjectInspector.jsx';
import { useNativeWorkspace } from './hooks/useNativeWorkspace.js';
import { needsSourceReplacement } from './lib/sourceEditPolicy.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon, Alert, Badge, Box, Button, Checkbox, ColorInput, Divider, Group,
  SegmentedControl,
  Menu, Modal, NumberInput, Paper, ScrollArea, Select, SimpleGrid, Stack,
  Text, Textarea, TextInput, ThemeIcon, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp, IconArrowForwardUp, IconArrowLeft, IconArrowRight, IconArrowsMove,
  IconCheck, IconChevronDown, IconCopy, IconCrop, IconDownload, IconFilePlus, IconFileTypePdf,
  IconFileZip, IconFiles, IconGripVertical, IconInfoCircle, IconLayersIntersect,
  IconLock, IconPhoto, IconPlus, IconRotate2, IconRotateClockwise,
  IconScissors, IconShieldCheck, IconTrash, IconTypography, IconUpload, IconX, IconEdit,
  IconZoomIn, IconZoomOut,
} from '@tabler/icons-react';
import { PdfCanvas } from './components/PdfCanvas.jsx';
import { DEFAULT_FONT_FAMILY, FONT_OPTIONS, fontShorthand } from './lib/fontCatalog.js';
import {
  buildPdf, cleanFileName, cropPage, importPdf, newBlankPage, pdfBlob,
  saveBlob, zipIndividualPages,
} from './lib/pdfEngine.js';
import { movePage, parsePageRanges } from './lib/ranges.js';
import { clamp, positionWithinPage, rotateAnnotation, visualBounds } from './lib/overlayGeometry.js';

const initialHistory = { past: [], present: [], future: [] };
const showError = (message) => notifications.show({ color: 'red', title: 'İşlem tamamlanamadı', message: message || 'Beklenmeyen hata.' });
const showSuccess = (message) => notifications.show({ color: 'teal', title: 'İşlem başarılı', message });
const zoomOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

function ToolButton({ icon: Icon, title, subtitle, onClick, disabled = false, active = false, danger = false }) {
  return (
    <button type="button" className={`tool-item ${active ? 'tool-active' : ''} ${danger ? 'tool-danger' : ''}`} onClick={onClick} disabled={disabled}>
      <span className="tool-icon"><Icon size={19} stroke={1.75} /></span>
      <span className="tool-info"><span className="tool-name">{title}</span><span className="tool-subtitle">{subtitle}</span></span>
    </button>
  );
}

function PageThumbnail({ page, index, selected, checked, sources, cache, onChoose, onCheck, onDropPage }) {
  return (
    <div
      draggable
      className={`page-tile ${selected ? 'page-tile-active' : ''}`}
      onDragStart={(event) => { event.dataTransfer.setData('application/x-pdf-studio-page', page.id); event.dataTransfer.effectAllowed = 'move'; }}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }}
      onDrop={(event) => {
        event.preventDefault(); event.stopPropagation();
        const from = event.dataTransfer.getData('application/x-pdf-studio-page');
        if (from) onDropPage(from, page.id);
      }}
    >
      <div className="page-tile-heading">
        <Checkbox size="xs" checked={checked} onChange={(event) => onCheck(page.id, event.currentTarget.checked)} aria-label={`${index + 1}. sayfayı seç`} />
        <span className="page-index">Sayfa {index + 1}</span>
        <IconGripVertical size={16} className="drag-handle" />
      </div>
      <button className="thumbnail-button" type="button" onClick={() => onChoose(page.id)} aria-label={`${index + 1}. sayfayı görüntüle`}>
        <PdfCanvas descriptor={page} sources={sources} cache={cache} thumbnail />
      </button>
    </div>
  );
}

/** Property controls are deliberately separate from the PDF rendering canvas.
 * Editing text updates its live preview; one undo step is committed on blur. */
function ObjectInspector({ item, bounds, onChange, onTextDraft, onReplaceImage, onCompositeMode, onDelete, onDuplicate, onClose }) {
  const [draftText, setDraftText] = useState(item.text || '');
  useEffect(() => { setDraftText(item.text || ''); }, [item.id, item.text]);
  function dimension(field, value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    onChange(item.id, { [field]: value });
  }
  function fitTextToBox() {
    const context=document.createElement('canvas').getContext('2d');
    if(!context) return;
    context.font=fontShorthand(item.fontSize,item.fontFamily,item);
    const lines=String(draftText).replace(/\r\n?/g,'\n').split('\n');
    const widest=Math.max(20,...lines.map(line=>context.measureText(line).width +
      Math.max(0,[...line].length-1)*(item.letterSpacing||0)));
    const desiredWidth=Math.min(bounds.width-item.x,Math.ceil(widest+14));
    const desiredHeight=Math.min(bounds.height-item.y,
      Math.ceil(lines.length*item.fontSize*(item.lineSpacing||1.25)+12));
    onChange(item.id, {text:draftText,width:Math.max(16,desiredWidth),height:Math.max(16,desiredHeight)});
  }
  const isText = item.type === 'text';
  return <aside className="object-inspector">
    <div className="inspector-heading"><div><strong>{isText ? 'Metin düzenle' : 'Görsel düzenle'}</strong><p>Seçili nesnenin özellikleri</p></div><ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Özellikleri kapat"><IconX size={18}/></ActionIcon></div>
    <ScrollArea className="inspector-scroll" type="auto"><Stack gap="md" p="md">
      <Alert color="indigo" variant="light" icon={<IconArrowsMove size={18}/>}>
        {item.compositeMode==='cover' ? 'Yerinde kaplama: konum ve boyut sabittir. Alttaki PDF değişmeden kalır.' : <>Nesneyi fareyle taşıyın. Köşe tutamaçlarından boyutlandırın.{!isText ? ' Görsellerin oranı korunur; Shift ile serbest boyutlandırılır.' : ''}</>}
      </Alert>
      {item.sourceRisk && item.type==='image' && <Stack gap="xs">
        <Alert color="yellow" variant="light">
          Bu nesne başka grafiklerle örtüşüyor veya bağımsız çıkarılamıyor. İşlem yöntemini seçin; kaynak PDF'deki içerikler silinmez.
        </Alert>
        <Button data-testid="composite-cover" size="xs" variant={item.compositeMode==='cover'?'filled':'light'}
          color="teal" onClick={()=>onCompositeMode(item.id,'cover')}>
          Yerinde değiştir (kaplama)
        </Button>
        <Button data-testid="composite-group" size="xs" variant={item.compositeMode==='group'?'filled':'light'}
          color="indigo" onClick={()=>onCompositeMode(item.id,'group')}>
          Birleşik alanı grup olarak taşı
        </Button>
        {item.compositeMode==='cover' && <Alert color="blue" variant="light">
          Dosya seçerek bu dikdörtgenin üzerine görsel ekleyin. Konumu ve boyutu sabit tutulur. PNG şeffaf alanları için arka plan rengini seçin. Altındaki PDF içeriği dosyada kalır.
        </Alert>}
        {item.compositeMode==='group' && <Alert color="orange" variant="light">
          Bu işlem fotoğrafla birlikte alanın içindeki yazı ve çizimleri tek görsel olarak taşır. Eski alanın zemini karmaşıksa kaydetme engellenir. Grup PDF'de sonradan seçilebilir yazı olarak kalmaz.
        </Alert>}
        {!item.compositeMode && <Text c="dimmed" size="xs">Yöntem seçilene kadar orijinal sayfa değiştirilmez.</Text>}
      </Stack>}
      {item.sourceRisk && item.type!=='image' && <Alert color="red" variant="light">{item.sourceRisk} Çevredeki grafikleri korumak için kaynak metin düzenlenemez.</Alert>}
      {item.originalRegion && !needsSourceReplacement(item) && !(item.compositeMode==='cover' && item.imageDirty) && <Alert color="green" variant="light">Nesne yalnızca seçildi. Orijinal PDF henüz değiştirilmedi.</Alert>}
      {item.originalRegion && item.compositeMode!=='cover' && <Alert color="orange" variant="light">
        Kaynak nesne değiştirilirse, eski yerinin yerel olarak onarılması gerekebilir. Karmaşık zeminler onarılamadığında indirme durdurulur. Bu yöntem gerçek PDF redaksiyonu değildir.
      </Alert>}
      {isText && <>
        {item.originalRegion && !item.editDirty && <Alert color="blue" variant="light">
          Özgün yazı görünümü korunuyor. Metni veya fontunu değiştirdiğinizde eşdeğer fontla yeniden çizilecek.
        </Alert>}
        <Select label="Yazı tipi / font ailesi" description="Seçtiğiniz font önizleme ve PDF kaydına uygulanır."
          data={FONT_OPTIONS} searchable allowDeselect={false}
          value={item.fontFamily || DEFAULT_FONT_FAMILY}
          onChange={(value) => { if (value) onChange(item.id, { fontFamily: value }); }} />
        <SimpleGrid cols={2} spacing="xs">
          <Checkbox label="Kalın" checked={!!item.bold} onChange={e=>onChange(item.id,{bold:e.currentTarget.checked})}/>
          <Checkbox label="İtalik" checked={!!item.italic} onChange={e=>onChange(item.id,{italic:e.currentTarget.checked})}/>
          <Checkbox label="Altı çizili" checked={!!item.underline} onChange={e=>onChange(item.id,{underline:e.currentTarget.checked})}/>
          <Checkbox label="Üstü çizili" checked={!!item.strikeThrough} onChange={e=>onChange(item.id,{strikeThrough:e.currentTarget.checked})}/>
        </SimpleGrid>
        <Text size="xs" fw={600}>Hizalama</Text>
        <SegmentedControl fullWidth value={item.textAlign||'left'} onChange={textAlign=>onChange(item.id,{textAlign})}
          data={[{label:'Sol',value:'left'},{label:'Orta',value:'center'},{label:'Sağ',value:'right'}]}/>
        <SimpleGrid cols={2} spacing="xs">
          <NumberInput label="Satır aralığı" min={1} max={3} step={0.05} decimalScale={2}
            value={item.lineSpacing||1.25} onChange={v=>dimension('lineSpacing',v)}/>
          <NumberInput label="Harf aralığı (pt)" min={-1} max={12} step={0.5}
            value={item.letterSpacing||0} onChange={v=>dimension('letterSpacing',v)}/>
        </SimpleGrid>
        <ColorInput label="Metin kutusu arka planı (isteğe bağlı)" format="hex"
          value={item.backgroundColor||'#ffffff'} onChange={backgroundColor=>onChange(item.id,{backgroundColor})}/>
        <Button size="xs" variant="subtle" color="gray" onClick={()=>onChange(item.id,{backgroundColor:null})}>Arka planı şeffaf yap</Button>
        <Textarea label="Metin içeriği" autosize minRows={4} maxRows={12} value={draftText}
          onChange={(event) => { setDraftText(event.currentTarget.value); onTextDraft(item.id, event.currentTarget.value); }}
          onBlur={() => { if (draftText !== item.text) onChange(item.id, { text: draftText }); onTextDraft(null, null); }} />
        <Button size="xs" variant="light" color="indigo" onClick={fitTextToBox}>
          Metin kutusunu yazıya uydur
        </Button>
        <SimpleGrid cols={2} spacing="xs">
          <NumberInput label="Yazı boyutu" min={6} max={144} value={item.fontSize} onChange={(n) => dimension('fontSize', n)} />
          <ColorInput label="Yazı rengi" format="hex" value={item.color} onChange={(color) => onChange(item.id, { color })} />
        </SimpleGrid>
      </>}
      {!isText && <>
        {item.selectionMode && item.selectionMode !== 'extracted' && !item.compositeMode && <Alert color="yellow" variant="light">Görsel bağımsız çıkarılamadı. Birleşik görsel yöntemlerinden birini seçerek kontrollü düzenleme yapabilirsiniz.</Alert>}
        {item.compositeMode==='cover' && <ColorInput label="Kaplama arka plan rengi" format="hex"
          value={item.coverColor||'#ffffff'} onChange={coverColor=>onChange(item.id,{coverColor})} />}
        <NumberInput label="Görünürlük / opaklık (%)" min={0} max={100} disabled={item.compositeMode==='cover'}
          value={Math.round((item.opacity ?? 1) * 100)}
          onChange={(n) => { if (typeof n === 'number') onChange(item.id, { opacity: clamp(n / 100, 0, 1) }); }} />
        <div className="inspector-image-file">
          <Text size="xs" fw={600}>Görseli değiştir</Text>
          <input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" aria-label="Görseli değiştir"
            onChange={(event) => { const file = event.target.files?.[0]; if (file) onReplaceImage(item.id, file); event.target.value = ''; }} />
        </div>
      </>}
      <Divider label="Konum ve boyut" labelPosition="center" />
      <SimpleGrid cols={2} spacing="xs">
        <NumberInput label="X konumu" disabled={item.compositeMode==='cover'} min={0} max={Math.floor(bounds.width)} decimalScale={1} value={Math.round(item.x)} onChange={(n) => dimension('x', n)} />
        <NumberInput label="Y konumu" disabled={item.compositeMode==='cover'} min={0} max={Math.floor(bounds.height)} decimalScale={1} value={Math.round(item.y)} onChange={(n) => dimension('y', n)} />
        <NumberInput label="Genişlik" disabled={item.compositeMode==='cover'} min={10} max={Math.ceil(bounds.width)} decimalScale={1} value={Math.round(item.width)} onChange={(n) => dimension('width', n)} />
        <NumberInput label="Yükseklik" disabled={item.compositeMode==='cover'} min={10} max={Math.ceil(bounds.height)} decimalScale={1} value={Math.round(item.height)} onChange={(n) => dimension('height', n)} />
      </SimpleGrid>
      <Text size="xs" c="dimmed">Ölçüler PDF punto birimindedir; %100 yakınlaştırmada doğrudan karşılık gelir.</Text>
      <Divider />
      <Button variant="light" color="indigo" leftSection={<IconCopy size={16}/>} onClick={() => onDuplicate(item.id)}>Nesneyi çoğalt</Button>
      <Button variant="light" color="red" disabled={item.compositeMode==='cover'} leftSection={<IconTrash size={16}/>} onClick={() => onDelete(item.id)}>Nesneyi sil</Button>
    </Stack></ScrollArea>
  </aside>;
}

function LegacyApp({ onOpenAudit, isAdmin }) {
  const navigate = useNavigate();
  const auditDocumentIdRef = useRef(null);
  const recordBrowserEvent = useCallback((action, details = {}) => {
    const id = auditDocumentIdRef.current;
    if (!id) return;
    // Browser-originated event; server records it with server_verified=false.
    void portalJson(`/documents/${id}/events`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, details }),
    }).catch((error) => console.warn('PDF Studio browser audit could not be logged:', error.message));
  }, []);
  const fileInputRef = useRef(null);
  const quickImageRef = useRef(null);
  const sourcesRef = useRef(new Map());
  const previewCacheRef = useRef(new Map());
  const imageUrlsRef = useRef(new Set());
  const [sourceMeta, setSourceMeta] = useState([]);
  const [history, setHistory] = useState(initialHistory);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [documentName, setDocumentName] = useState('PDF-Studio');
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [extractOpened, setExtractOpened] = useState(false);
  const [extractRange, setExtractRange] = useState('');
  const [cropOpened, setCropOpened] = useState(false);
  const [margins, setMargins] = useState({ left: 5, right: 5, top: 5, bottom: 5 });
  const [dragOver, setDragOver] = useState(false);
  const [editMode,setEditMode] = useState(null);

  const pages = history.present;
  const selectedPage = pages.find((page) => page.id === selectedId) || pages[0];
  const selectedIndex = pages.findIndex((page) => page.id === selectedPage?.id);
  const activeOverlay = selectedPage?.overlays?.find((item) => item.id === selectedOverlayId) || null;
  const selectedSet = useMemo(() => new Set(checkedIds), [checkedIds]);

  const commit = useCallback((next) => {
    setHistory((current) => ({
      past: [...current.past.slice(-24), current.present],
      present: next,
      future: [],
    }));
  }, []);

  // Native editing is integrated into this SAME page.  After every server edit
  // the updated PDF is re-imported into classic history; there is only one
  // document and only one export button.
  const applyNativePdf = useCallback(async (file, previousIndex) => {
    const {source, pages: updatedPages} = await importPdf(file);
    sourcesRef.current.set(source.id, source);
    previewCacheRef.current.clear();
    commit(updatedPages);
    setSelectedId(updatedPages[Math.min(Math.max(0, previousIndex), updatedPages.length - 1)]?.id || null);
    setSelectedOverlayId(null);
    setTextDraft(null);
    setSourceMeta([{ id: source.id, name: file.name, count: updatedPages.length, size: file.size }]);
    showSuccess('Özgün PDF içeriği düzenlendi. Bütün araçlar aynı çalışma alanında kullanılabilir.');
  }, [commit]);

  const native = useNativeWorkspace({
    enabled: editMode === 'native' || editMode === 'native-images',
    pages, sourcesRef, documentName, selectedIndex, onPdfChanged: applyNativePdf,
  });




  const execute = useCallback(async (callback) => {
    if (busy) return;
    setBusy(true);
    try { await callback(); }
    catch (error) { console.error(error); showError(error?.message); }
    finally { setBusy(false); }
  }, [busy]);

  const openFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name));
    if (!files.length) { showError('Lütfen .pdf uzantılı bir dosya seçin.'); return; }
    execute(async () => {
      const addedPages = [];
      const newMeta = [];
      const rejected = [];
      for (const file of files) {
        try {
          // Server side document upload is mandatory for user attribution/audit.
          const data = new FormData();
          data.append('file', file, file.name);
          const uploaded = await nativeJson('/documents', { method: 'POST', body: data });
          const { source, pages: importedPages } = await importPdf(file);
          auditDocumentIdRef.current = uploaded.id;
          sourcesRef.current.set(source.id, source);
          newMeta.push({ id: source.id, name: file.name, count: importedPages.length, size: file.size });
          addedPages.push(...importedPages);
        } catch (error) {
          rejected.push(`${file.name}: ${error?.message || 'Dosya okunamadı.'}`);
        }
      }
      if (addedPages.length) {
        if (pages.length || addedPages.length > 1) recordBrowserEvent('PDF_MERGE', { count: files.length });
        const emptyBefore = pages.length === 0;
        commit([...pages, ...addedPages]);
        if (emptyBefore) { setSelectedId(addedPages[0].id); setDocumentName(files[0].name.replace(/\.pdf$/i, '')); }
        setSourceMeta((previous) => [...previous, ...newMeta]);
        showSuccess(`${addedPages.length} sayfa çalışma alanına eklendi.`);
      }
      if (rejected.length) showError(rejected.join(' | '));
    });
  }, [execute, pages, commit, recordBrowserEvent]);

  function onFileSelected(event) {
    if (event.target.files?.length) openFiles(event.target.files);
    event.target.value = '';
  }

  function resetProject() {
    if (pages.length && !window.confirm('Çalışma alanı temizlensin mi? Kaydedilmemiş değişiklikler kaybolur.')) return;
    sourcesRef.current.clear();
    previewCacheRef.current.clear();
    setHistory(initialHistory);
    setSourceMeta([]);
    setSelectedId(null);
    setCheckedIds([]);
    setDocumentName('PDF-Studio');
    setSelectedOverlayId(null);
    setTextDraft(null);
    setEditMode(null);
    auditDocumentIdRef.current = null;
    for (const url of imageUrlsRef.current) URL.revokeObjectURL(url);
    imageUrlsRef.current.clear();
  }

  function undo() {
    setHistory((current) => {
      if (!current.past.length) return current;
      return { past: current.past.slice(0, -1), present: current.past.at(-1), future: [current.present, ...current.future] };
    });
  }
  function redo() {
    setHistory((current) => {
      if (!current.future.length) return current;
      return { past: [...current.past, current.present], present: current.future[0], future: current.future.slice(1) };
    });
  }

  useEffect(() => {
    if (!pages.length) { setSelectedId(null); setSelectedOverlayId(null); setCheckedIds([]); return; }
    if (!pages.some((page) => page.id === selectedId)) setSelectedId(pages[0].id);
    setCheckedIds((ids) => ids.filter((id) => pages.some((page) => page.id === id)));
    if (selectedOverlayId && !selectedPage?.overlays?.some((item) => item.id === selectedOverlayId)) setSelectedOverlayId(null);
  }, [pages, selectedId, selectedOverlayId, selectedPage]);

  useEffect(() => {
    function onKeyDown(event) {
      const tag = document.activeElement?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || document.activeElement?.isContentEditable) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault(); redo();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []); // undo/redo use functional history updates

  useEffect(() => {
    function onDeleteOrEscape(event) {
      const node = document.activeElement;
      if (node && (['INPUT', 'TEXTAREA', 'SELECT'].includes(node.tagName) || node.isContentEditable)) return;
      if (event.key === 'Escape') {
     setSelectedOverlayId(null); setTextDraft(null);
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedOverlayId && selectedPage && !busy) {
        event.preventDefault();
        const target = selectedPage.overlays?.find(o => o.id === selectedOverlayId);
        if(target?.sourceRisk && target.compositeMode!=='group') {showError(target.compositeMode==='cover'?'Kaplama silinirse alttaki orijinal içerik görünür. Bunun için Geri Al kullanın.':target.sourceRisk);return;}
        const nextPages = pages.map(p => p.id === selectedPage.id
          ? { ...p, overlays: (p.overlays || []).flatMap(o => o.id !== selectedOverlayId
             ? [o] : o.originalRegion ? [{...o,hidden:true,removedSource:true}] : []) } : p);
        commit(nextPages);
        setSelectedOverlayId(null);
      }
    }
    window.addEventListener('keydown', onDeleteOrEscape);
    return () => window.removeEventListener('keydown', onDeleteOrEscape);
  }, [selectedOverlayId, selectedPage, pages, busy, commit]);

  function rotate(angle) {
    if (!selectedPage) return;
    recordBrowserEvent('PAGE_ROTATE', { page: selectedIndex });
    const before = visualBounds(selectedPage);
    commit(pages.map((page) => page.id === selectedPage.id
      ? { ...page, rotation: page.rotation + angle,
          overlays: (page.overlays || []).map((item) => ({ ...rotateAnnotation(item, before, angle),
            ...(item.originalRegion ? {originalRegion:rotateAnnotation(item.originalRegion,before,angle)} : {}) })) }
      : page));
  }

  function updateOverlay(id, patch) {
    if (!selectedPage) return;
    const atRisk = (selectedPage.overlays || []).find(item => item.id === id);
    if (atRisk?.compositeMode==='cover' && Object.keys(patch).some(k => ['x','y','width','height','opacity'].includes(k))) {
      showError('Yerinde değiştirme modunda konum, boyut ve opaklık kilitlidir. Grup olarak taşı modunu kullanın.');
      return;
    }
    if (atRisk?.sourceRisk && !['cover','group'].includes(atRisk.compositeMode) && Object.keys(patch).some(k =>
      ['x','y','width','height','text','fontFamily','fontSize','color','bold',
        'italic','underline','strikeThrough','textAlign','letterSpacing','lineSpacing',
        'backgroundColor','bytes','previewUrl','opacity'].includes(k))) {
      showError(atRisk.sourceRisk + ' Bu bileşik PDF içeriği güvenle ayrılamadığı için orijinal korunuyor.');
      return;
    }
    const bounds = visualBounds(selectedPage);
    const changesAppearance = Object.keys(patch).some(field => [
      'text','fontFamily','fontSize','color','bold','italic','underline',
      'strikeThrough','textAlign','letterSpacing','lineSpacing','backgroundColor',
    ].includes(field));
    const next = (selectedPage.overlays || []).map((item) => item.id === id
      ? positionWithinPage({ ...item, ...patch,
          ...(item.type==='text' && item.originalRegion && changesAppearance ? {editDirty:true} : {}),
          ...(item.type==='image' && item.originalRegion && Object.keys(patch).some(key => ['bytes','mime','previewUrl','opacity'].includes(key)) ? {imageDirty:true} : {}) }, bounds)
      : item);
    const existing = selectedPage.overlays?.find((item) => item.id === id);
    const updated = next.find((item) => item.id === id);
    if (!existing || !updated || Object.keys(patch).every((key) => updated[key] === existing[key])) return;
    commit(pages.map((page) => page.id === selectedPage.id ? { ...page, overlays: next } : page));
  }

  function replaceOverlayImage(id, file) {
    if (!selectedPage) return;
    execute(async () => {
      const mime = file.type || (/\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg');
      if (!['image/png', 'image/jpeg'].includes(mime)) throw new Error('Yalnızca PNG/JPG görseller destekleniyor.');
      if (file.size > 20 * 1024 * 1024) throw new Error('Tek görsel 20 MB sınırını geçemez.');
      const size = await decodeImageSize(file);
      const ratio = size.height / size.width;
      const previewUrl = URL.createObjectURL(file);
      imageUrlsRef.current.add(previewUrl);
      const previous = selectedPage.overlays?.find((item) => item.id === id);
      if (!previous) return;
      if (previous.sourceRisk && !['cover','group'].includes(previous.compositeMode)) throw new Error(previous.sourceRisk + ' Önce birleşik görsel düzenleme yöntemini seçin.');
      const width = previous.width;
      updateOverlay(id, { mime, bytes: new Uint8Array(await file.arrayBuffer()), previewUrl,
        ...(previous.compositeMode==='cover' ? {} : {height:width * ratio}) });
      showSuccess(previous.compositeMode==='cover'?'Görsel eski alanın üzerine yerleştirildi. PDF’yi indirerek kontrol edin.':'Görsel değiştirildi. Konumu ve diğer özellikleri düzenleyebilirsiniz.');
    });
  }

  function deleteOverlay(id) {
    if (!selectedPage) return;
    const target = selectedPage.overlays?.find(o => o.id === id);
    if (target?.sourceRisk && target.compositeMode!=='group') { showError(target.compositeMode==='cover'?'Kaplamayı Geri Al ile kaldırabilirsiniz.':target.sourceRisk); return; }
    commit(pages.map((page) => page.id === selectedPage.id
      ? { ...page, overlays: (page.overlays || []).flatMap(item => item.id !== id
        ? [item] : item.originalRegion ? [{...item,hidden:true,removedSource:true}] : []) } : page));
    setSelectedOverlayId(null);
  }

  function duplicateOverlay(id) {
    if (!selectedPage) return;
    const src = selectedPage.overlays?.find((item) => item.id === id);
    if (!src) return;
    const duplicate = positionWithinPage({ ...src, originalRegion:null, removedSource:false,
      sourceObjectKey:null, sourceRisk:null, compositeMode:null,
      id: crypto.randomUUID(), x: src.x + 18, y: src.y + 18 }, visualBounds(selectedPage));
    commit(pages.map((page) => page.id === selectedPage.id
      ? { ...page, overlays: [...page.overlays, duplicate] } : page));
    setSelectedOverlayId(duplicate.id);
  }

  function deleteSelected() {
    if (!selectedPage) return;
    const toRemove = checkedIds.length ? checkedIds : [selectedPage.id];
    const next = pages.filter((page) => !toRemove.includes(page.id));
    commit(next);
    recordBrowserEvent('PAGE_DELETE', { count: toRemove.length });
    setCheckedIds([]);
    setSelectedId(next[Math.min(selectedIndex, next.length - 1)]?.id || null);
  }

  function duplicatePage() {
    if (!selectedPage) return;
    const duplicate = { ...selectedPage, id: crypto.randomUUID(), overlays: (selectedPage.overlays || []).map((o) => ({ ...o, id: crypto.randomUUID() })) };
    const next = [...pages];
    next.splice(selectedIndex + 1, 0, duplicate);
    commit(next);
    recordBrowserEvent('ADD_PAGE', { pages_count: next.length });
    setSelectedId(duplicate.id);
    setSelectedOverlayId(null);
    showSuccess('Sayfanın bir kopyası eklendi.');
  }

  function insertBlank() {
    recordBrowserEvent('ADD_PAGE', { pages_count: pages.length + 1 });
    const blank = newBlankPage();
    const next = [...pages];
    next.splice(selectedPage ? selectedIndex + 1 : 0, 0, blank);
    commit(next);
    setSelectedId(blank.id);
    showSuccess('Boş A4 sayfası eklendi.');
  }

  function goPage(delta) {
    const page = pages[Math.max(0, Math.min(pages.length - 1, selectedIndex + delta))];
    if (page) setSelectedId(page.id);
  }

  function chooseZoom(delta) {
    const index = zoomOptions.findIndex((value) => value >= zoom);
    const next = zoomOptions[Math.max(0, Math.min(zoomOptions.length - 1, index + delta))];
    setZoom(next);
  }

  function downloadAll() {
    execute(async () => {
      const bytes = await buildPdf(pages, sourcesRef.current);
      saveBlob(pdfBlob(bytes), `${cleanFileName(documentName)}.pdf`);
      recordBrowserEvent('PDF_EXPORT', { pages_count: pages.length });
      showSuccess('PDF dosyası bilgisayarınıza indirildi.');
    });
  }

  function exportRanges() {
    execute(async () => {
      const indices = parsePageRanges(extractRange, pages.length);
      const bytes = await buildPdf(indices.map((index) => pages[index]), sourcesRef.current);
      saveBlob(pdfBlob(bytes), `${cleanFileName(documentName)}-secili-sayfalar.pdf`);
      setExtractOpened(false);
      recordBrowserEvent('PDF_SPLIT', { count: indices.length });
      showSuccess(`${indices.length} sayfa ayrı PDF olarak indirildi.`);
    });
  }

  function exportChecked() {
    execute(async () => {
      const chosen = pages.filter((page) => selectedSet.has(page.id));
      if (!chosen.length) throw new Error('Önce soldan en az bir sayfa işaretleyin.');
      const bytes = await buildPdf(chosen, sourcesRef.current);
      saveBlob(pdfBlob(bytes), `${cleanFileName(documentName)}-secim.pdf`);
      showSuccess(`${chosen.length} sayfa dışa aktarıldı.`);
    });
  }

  function splitAll() {
    execute(async () => {
      const blob = await zipIndividualPages(pages, sourcesRef.current, cleanFileName(documentName));
      saveBlob(blob, `${cleanFileName(documentName)}-sayfalar.zip`);
      recordBrowserEvent('PDF_SPLIT', { count: pages.length });
      showSuccess('Her sayfa ayrı PDF olacak şekilde ZIP indirildi.');
    });
  }

  function cropCurrent() {
    if (!selectedPage) return;
    execute(async () => {
      const before = visualBounds(selectedPage);
      const result = await cropPage(selectedPage, sourcesRef.current, margins);
      sourcesRef.current.set(result.source.id, result.source);
      const newPage = result.pages[0];
      const shiftX = before.width * margins.left / 100;
      const shiftY = before.height * margins.top / 100;
      const overlays = (selectedPage.overlays || []).map((item) => ({
        ...item, x: item.x - shiftX, y: item.y - shiftY,
        originalRegion:item.originalRegion ? { ...item.originalRegion,
          x:item.originalRegion.x-shiftX,y:item.originalRegion.y-shiftY } : null,
      }));
      const updated = { ...newPage, overlays };
      commit(pages.map((page) => page.id === selectedPage.id ? updated : page));
      setSelectedId(updated.id);
      setSelectedOverlayId(null);
      setCropOpened(false);
      showSuccess('Sayfa görünür alanı kırpıldı. Eklenen nesneler düzenlenebilir kaldı.');
    });
  }

  // New editor workflow: inserts an editable object immediately, no modal or
  // hidden second "click on page" step. The user can move and resize it at once.
  // PDF's pre-existing text/image becomes a movable object on first click.
  // When text is only moved, a pixel-perfect snapshot keeps its original font.
  // Changing text switches to a matching available browser font (best effort).
  function pickExistingText(run,blob,appearance) {
    if(!selectedPage || selectedPage.kind!=='pdf') return;
    execute(async()=>{
      const bounds=visualBounds(selectedPage);
      const originalRegion={x:run.x,y:run.y,width:run.width,height:run.height};
      const originalPreviewUrl=blob ? URL.createObjectURL(blob) : null;
      if(originalPreviewUrl) imageUrlsRef.current.add(originalPreviewUrl);
      const item=positionWithinPage({id:crypto.randomUUID(),type:'text',text:run.text,
        fontFamily:run.fontFamily || DEFAULT_FONT_FAMILY,fontSize:run.fontSize,
        color:appearance?.color || '#20242c',bold:!!run.bold,italic:!!run.italic,underline:false,strikeThrough:false,
        textAlign:'left',lineSpacing:1.0,letterSpacing:0,
        x:run.x,y:run.y,width:run.width,height:run.height,
        originalRegion,sourceObjectKey:run.id,sourceRisk:run.sourceRisk||null,originalPreviewUrl,
        originalSnapshotBytes:blob ? new Uint8Array(await blob.arrayBuffer()) : null,
        editDirty:false,pdfFontName:run.pdfFontName||'',sourceBackground:appearance?.background||null,
      },bounds);
      commit(pages.map(p=>p.id===selectedPage.id
        ? {...p,overlays:[...(p.overlays||[]),item]} : p));
      setSelectedOverlayId(item.id);setTextDraft(null);
      showSuccess('PDF metni seçildi. Özgün yazı rengi algılandı; taşıyabilir veya sağdan değiştirebilirsiniz.');
    });
  }
  function pickExistingImage(rect,pick) {
    if(!selectedPage || selectedPage.kind!=='pdf') return;
    execute(async()=>{
      const blob=pick?.blob;
      const previewUrl=blob ? URL.createObjectURL(blob) : null;
      if(previewUrl) imageUrlsRef.current.add(previewUrl);
      const groupPreviewUrl=pick?.groupBlob ? URL.createObjectURL(pick.groupBlob) : null;
      if(groupPreviewUrl) imageUrlsRef.current.add(groupPreviewUrl);
      const item={id:crypto.randomUUID(),type:'image',previewUrl,
        mime:'image/png',bytes:blob ? new Uint8Array(await blob.arrayBuffer()) : new Uint8Array(),opacity:1,
        x:rect.x,y:rect.y,width:rect.width,height:rect.height,
        originalRegion:{x:rect.x,y:rect.y,width:rect.width,height:rect.height},
        sourceOriginalRegion:{x:rect.x,y:rect.y,width:rect.width,height:rect.height},
        selectionMode:pick?.selectionMode||'placeholder',
        sourceObjectKey:rect.id,sourceRisk:pick?.sourceRisk||null,
        groupPreviewUrl, groupPreviewBytes:pick?.groupBlob ? new Uint8Array(await pick.groupBlob.arrayBuffer()) : null,
        groupRegion:pick?.groupRegion||{x:rect.x,y:rect.y,width:rect.width,height:rect.height},
        groupBackgroundFlat:pick?.groupBackgroundFlat??null,
        coverColor:'#ffffff',compositeMode:null};
      commit(pages.map(p=>p.id===selectedPage.id
        ? {...p,overlays:[...(p.overlays||[]),item]} : p));
      setSelectedOverlayId(item.id);
      if(pick?.selectionMode==='extracted' && !pick?.sourceRisk) {
        showSuccess('PDF görseli seçildi. Kaynak piksel verisi çıkarıldı; seçili öğeyi düzenleyebilirsiniz.');
      } else if(pick?.selectionMode==='extracted') {
        notifications.show({color:'yellow',title:'Görsel seçildi, bileşik grafik',message:'Görsel seçildi; çevredeki metin veya çizimlerle birleşik olduğu için dışa aktarma işlemi korunuyor.'});
      } else {
        notifications.show({color:'yellow',title:'Görsel önizlemeden seçildi',message:'Kaynak görsel ayrıştırılamadı. Bu öğenin düzenlenmiş hali güvenli biçimde PDF’ye kaydedilemez.'});
      }
    });
  }

  function enableCompositeMode(id, mode) {
    if(!selectedPage) return;
    const current=selectedPage.overlays?.find(o=>o.id===id);
    if (!current || current.type!=='image' || !current.sourceRisk) return;
    if(!['group','cover'].includes(mode)) return;
    if(mode==='group' && current.groupBackgroundFlat===false) {
      showError('Seçilen grubun eski konumu desenli/karmaşık bir zemin üzerinde. Güvenli taşıma için ayrılamıyor; yerinde değiştir seçeneğini kullanın.');
      return;
    }
    if(mode==='group' && !current.groupPreviewBytes?.length) {
      showError('Bu alanın bütünleşik görüntüsü alınamadı. Yerinde değiştir seçeneğini kullanabilirsiniz.');
      return;
    }
    const area=mode==='group' ? current.groupRegion : current.sourceOriginalRegion;
    const updated={...current, compositeMode:mode,
      x:area.x,y:area.y,width:area.width,height:area.height,
      originalRegion:{...area}, opacity:1,
      ...(mode==='group' ? {bytes:current.groupPreviewBytes,previewUrl:current.groupPreviewUrl,mime:'image/png'} : {}),
      imageDirty:false};
    commit(pages.map(p=>p.id===selectedPage.id
      ? {...p,overlays:p.overlays.map(o=>o.id===id?updated:o)} : p));
    showSuccess(mode==='cover'?'Kaplama modu seçildi. Görseli değiştir alanından yeni dosyayı yükleyin.':'Görsel grubu oluşturuldu. Fareyle taşıyabilirsiniz. Karmaşık zeminlerde kaydetme güvenli biçimde engellenir.');
  }

  function quickAddText() {
    if (!selectedPage || busy || native.busy) return;
    setEditMode(null); native.choose(null);
    const bounds = visualBounds(selectedPage);
    const width = Math.min(260, bounds.width * 0.76);
    const height = Math.min(92, bounds.height * 0.25);
    const item = positionWithinPage({
      id: crypto.randomUUID(), type: 'text',
      // Added overlay, not an existing PDF source object.
      originalRegion: null,
      text: 'Metninizi buraya yazın', color: '#1e293b', fontSize: 20, fontFamily: DEFAULT_FONT_FAMILY,
      bold:false,italic:false,underline:false,strikeThrough:false,textAlign:'left',lineSpacing:1.25,letterSpacing:0,
      x: (bounds.width - width) / 2, y: (bounds.height - height) / 2,
      width, height,
    }, bounds);
    commit(pages.map(p => p.id === selectedPage.id
      ? { ...p, overlays: [...(p.overlays || []), item] } : p));
    setSelectedOverlayId(item.id);
    setTextDraft(null);
    recordBrowserEvent('ADD_TEXT', { page: selectedIndex });
    showSuccess('Metin eklendi. Metin kutusunun içinden veya Taşı tutamacından sürükleyin; sağdan fontu değiştirin.');
  }

  async function decodeImageSize(file) {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Görsel okunamadı. Başka bir PNG/JPG deneyin.'));
        img.src = url;
      });
      return { width: img.naturalWidth, height: img.naturalHeight };
    } finally { URL.revokeObjectURL(url); }
  }

  function quickAddImage(file) {
    if (!file || !selectedPage || busy || native.busy) return;
    setEditMode(null); native.choose(null);
    execute(async () => {
      const mime = file.type || (/\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg');
      if (!['image/png', 'image/jpeg'].includes(mime)) throw new Error('Yalnızca PNG ve JPG görseller desteklenir.');
      if (file.size > 20 * 1024 * 1024) throw new Error("Görsel boyutu 20 MB'ı aşamaz.");
      const size = await decodeImageSize(file);
      if (!size.width || !size.height) throw new Error('Görselin boyutu okunamadı.');
      const bounds = visualBounds(selectedPage);
      const ratio = size.height / size.width;
      const width = Math.min(200, bounds.width * 0.65, bounds.height * 0.42 / ratio);
      const height = width * ratio;
      const url = URL.createObjectURL(file);
      imageUrlsRef.current.add(url);
      const item = positionWithinPage({
        id: crypto.randomUUID(), type: 'image',
        mime, bytes: new Uint8Array(await file.arrayBuffer()), previewUrl: url,
        opacity: 1, x: (bounds.width - width) / 2, y: (bounds.height - height) / 2,
        width, height,
      }, bounds);
      commit(pages.map(p => p.id === selectedPage.id
        ? { ...p, overlays: [...(p.overlays || []), item] } : p));
      setSelectedOverlayId(item.id);
      recordBrowserEvent('ADD_IMAGE', { page: selectedIndex });
      showSuccess('Görsel eklendi. Fareyle taşıyın, köşelerden yeniden boyutlandırın.');
    });
  }

  function handleOuterDrop(event) {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files?.length) openFiles(event.dataTransfer.files);
  }

  const checkedCount = checkedIds.length;
  return (
    <div className="app-layout" onDragOver={(event) => { if (event.dataTransfer.types.includes('Files')) { event.preventDefault(); setDragOver(true); } }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(false); }} onDrop={handleOuterDrop}>
      <input ref={fileInputRef} hidden type="file" accept=".pdf,application/pdf" multiple onChange={onFileSelected} />
      {dragOver && <div className="global-file-drop"><IconUpload size={46} /><h2>PDF dosyalarını buraya bırakın</h2><p>PDF dosyaları portal sunucusuna da kaydedilir</p></div>}

      <aside className="sidebar">
        <div className="brand"><div className="brand-symbol"><IconFileTypePdf size={25}/></div><div><strong>PDF Studio</strong><small>Birleşik Editör v1.1</small></div></div>
        <div className="sidebar-body">
          <button className="upload-button" onClick={() => fileInputRef.current?.click()} disabled={busy}><IconUpload size={19} /> PDF dosyası ekle <IconPlus size={17}/></button>
          <div className="side-section-heading">BELGE İŞLEMLERİ</div>
          <ToolButton icon={IconFiles} title="PDF birleştir" subtitle="Birden fazla dosya ekle" onClick={() => fileInputRef.current?.click()} disabled={busy} />
          <ToolButton icon={IconScissors} title="Sayfaları ayır" subtitle="Aralık ile dışa aktar" onClick={() => { setExtractRange(checkedIds.length ? pages.map((p, i) => selectedSet.has(p.id) ? i + 1 : null).filter(Boolean).join(',') : `1-${pages.length}`); setExtractOpened(true); }} disabled={!pages.length || busy} />
          <ToolButton icon={IconFileZip} title="Tüm sayfaları böl" subtitle="Ayrı PDF'ler (ZIP)" onClick={splitAll} disabled={!pages.length || busy} />
          <ToolButton icon={IconFilePlus} title="Boş sayfa ekle" subtitle="Seçili sayfadan sonra" onClick={insertBlank} disabled={busy} />
          <ToolButton icon={IconCopy} title="Sayfayı çoğalt" subtitle="Seçili sayfanın kopyasını ekle" onClick={duplicatePage} disabled={!selectedPage || busy} />
          <div className="side-section-heading space-above">SAYFA DÜZENLEME</div>
          <ToolButton icon={IconEdit} title="PDF içeriğini düzenle" subtitle="Özgün yazı ve görsele tıkla" active={!!editMode} onClick={() => {setSelectedOverlayId(null);setEditMode(v => v ? null : 'native');}} disabled={!selectedPage || busy} />
          <ToolButton icon={IconTypography} title="Metin ekle" subtitle="Taşı, boyutlandır, düzenle" onClick={quickAddText} disabled={!selectedPage || busy || native.busy} />
          <ToolButton icon={IconPhoto} title="Görsel / imza ekle" subtitle="Sürükle ve yeniden boyutlandır" onClick={() => quickImageRef.current?.click()} disabled={!selectedPage || busy || native.busy} />
          <ToolButton icon={IconCrop} title="Sayfa kırp" subtitle="Kenar boşluklarını belirle" onClick={() => setCropOpened(true)} disabled={!selectedPage || busy} />
          <ToolButton icon={IconTrash} title="Sayfa sil" subtitle={checkedCount ? `${checkedCount} seçili sayfayı sil` : 'Görüntülenen sayfayı sil'} onClick={deleteSelected} disabled={!selectedPage || busy} danger />
        </div>
        <div className="privacy-panel"><IconShieldCheck size={20} /><div><strong>Kurumsal işlem kaydı</strong><span>Dosya yükleme ve sunucu düzenlemeleri kullanıcı hesabınıza bağlanır.</span></div></div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="top-left"><div className="breadcrumb">SMS Grup Portal <span>/</span> <strong>PDF Studio</strong></div><Badge size="sm" color="teal" variant="light" leftSection={<IconLock size={12} />}>JWT korumalı</Badge></div>
          <Group gap="xs" wrap="nowrap">
            <Button variant="subtle" color="gray" size="xs" leftSection={<IconArrowLeft size={15}/>} onClick={() => navigate("/")}>Portala Dön</Button>
            <Tooltip label="Geri al (Ctrl+Z)"><ActionIcon variant="subtle" color="gray" size="lg" onClick={undo} disabled={!history.past.length || busy}><IconArrowBackUp size={19}/></ActionIcon></Tooltip>
            <Tooltip label="Yinele (Ctrl+Shift+Z)"><ActionIcon variant="subtle" color="gray" size="lg" onClick={redo} disabled={!history.future.length || busy}><IconArrowForwardUp size={19}/></ActionIcon></Tooltip>
            {isAdmin && <Button variant="light" size="xs" onClick={onOpenAudit}>İşlem Kayıtları</Button>}
            <Divider orientation="vertical" />
            <Button leftSection={<IconDownload size={18}/>} disabled={!pages.length || busy || native.busy} loading={busy || native.busy} onClick={downloadAll}>PDF İndir</Button>
          </Group>
        </header>

        <div className="document-bar">
          <div className="document-title"><div className="document-icon"><IconFileTypePdf size={23}/></div><div className="doc-title-fields"><TextInput aria-label="Dosya adı" value={documentName} onChange={(event) => setDocumentName(event.currentTarget.value)} variant="unstyled" className="doc-name-input" /><span>{pages.length ? `${pages.length} sayfa · ${sourceMeta.length} kaynak PDF · Kaydedilmemiş çalışma` : 'Yeni çalışma alanı'}</span></div></div>
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Yeni çalışma"><ActionIcon size="lg" color="gray" variant="light" onClick={resetProject} disabled={busy}><IconPlus size={18}/></ActionIcon></Tooltip>
            <Button color="gray" variant="light" leftSection={<IconUpload size={17}/>} onClick={() => fileInputRef.current?.click()} disabled={busy}>Dosya ekle</Button>
          </Group>
        </div>

        {pages.length === 0 ? (
          <div className="empty-stage">
            <div className="empty-illustration"><IconFileTypePdf size={72} stroke={1.1}/><span className="empty-illustration-plus"><IconPlus size={22}/></span></div>
            <Badge color="indigo" variant="light" size="lg">Tüm PDF işlemleri tek ekranda</Badge>
            <h1>PDF'lerinizi kolayca düzenleyin</h1>
            <p>Dosyaları birleştirin, sayfaları bölün, sıralayın, döndürün, kırpın ve metin veya imza ekleyin. Belgeler tarayıcıda ve kimlik doğrulamalı portal PDF motorunda işlenir.</p>
            <button type="button" className="big-drop-zone" onClick={() => fileInputRef.current?.click()} disabled={busy}>
              <span className="big-drop-icon"><IconUpload size={26}/></span><strong>PDF seçin veya buraya sürükleyin</strong><span>Birden fazla PDF dosyası seçebilirsiniz</span><span className="drop-button">Dosyalara göz at</span>
            </button>
            <button className="create-blank-link" type="button" onClick={insertBlank}><IconFilePlus size={17}/> Boş A4 belgeyle başla</button>
            <div className="empty-feature-row"><span><IconShieldCheck size={16}/> Kullanıcı bazlı denetim kaydı</span><span><IconLayersIntersect size={16}/> Vektörel PDF koruması</span><span><IconCheck size={16}/> Portal entegrasyonu</span></div>
          </div>
        ) : (
          <div className="workspace">
            <section className="page-rail">
              <div className="rail-header"><div><strong>Sayfalar</strong><p>{pages.length} sayfa {checkedCount ? `• ${checkedCount} işaretli` : ''}</p></div><Menu position="bottom-end" shadow="md"><Menu.Target><ActionIcon color="gray" variant="subtle"><IconChevronDown size={19}/></ActionIcon></Menu.Target><Menu.Dropdown><Menu.Item onClick={() => setCheckedIds(pages.map((p) => p.id))}>Tümünü işaretle</Menu.Item><Menu.Item onClick={() => setCheckedIds([])}>Seçimi kaldır</Menu.Item><Menu.Item onClick={exportChecked} disabled={!checkedCount}>İşaretlenenleri PDF olarak indir</Menu.Item></Menu.Dropdown></Menu></div>
              <div className="rail-hint"><IconArrowsMove size={15}/> Sayfaları sürükleyerek sıralayın</div>
              <ScrollArea className="thumbnail-scroll" type="auto">
                <div className="thumbnail-list">
                  {pages.map((page, index) => (
                    <PageThumbnail key={page.id} page={page} index={index} selected={selectedPage?.id === page.id} checked={selectedSet.has(page.id)} sources={sourcesRef.current} cache={previewCacheRef.current} onChoose={setSelectedId} onCheck={(id, checked) => setCheckedIds((prev) => checked ? [...new Set([...prev, id])] : prev.filter((item) => item !== id))} onDropPage={(sourceId, targetId) => { if (busy || sourceId === targetId) return; commit(movePage(pages, sourceId, targetId)); recordBrowserEvent('PAGE_REORDER', { count: pages.length }); }} />
                  ))}
                </div>
              </ScrollArea>
              <div className="rail-footer"><Button color="gray" variant="light" size="xs" fullWidth leftSection={<IconDownload size={15}/>} disabled={!checkedCount || busy} onClick={exportChecked}>Seçili sayfaları indir {checkedCount ? `(${checkedCount})` : ''}</Button></div>
            </section>

            <section className="editor-area">
              <div className="editor-toolbar editor-toolbar-v04">
                <div className="editor-primary-actions">
                  <Button data-testid="add-text" color="indigo" leftSection={<IconTypography size={18}/>} onClick={quickAddText} disabled={!selectedPage || busy || native.busy}>Metin ekle</Button>
                  <Button data-testid="add-image" variant="light" color="indigo" leftSection={<IconPhoto size={18}/>} onClick={() => quickImageRef.current?.click()} disabled={!selectedPage || busy || native.busy}>Görsel ekle</Button>
                  <input ref={quickImageRef} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" hidden
                    aria-label="PDF için görsel seç" onChange={event => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ''; if (file) quickAddImage(file); }}/>
                  <Button data-testid="pdf-edit-mode" variant={editMode?'filled':'outline'}
                    color="teal" leftSection={<IconEdit size={18}/>}
                    disabled={!selectedPage || selectedPage.kind!=='pdf' || busy || native.busy}
                    onClick={()=>{setSelectedOverlayId(null);native.choose(null);setEditMode(v=>v?null:'native');}}>
                    {editMode?'PDF Düzenle ✓':'PDF Düzenle'}
                  </Button>
                  {editMode && <Button data-testid="pdf-image-select" variant={editMode==='native-images'?'filled':'light'}
                    color="cyan" leftSection={<IconPhoto size={17}/>} size="sm"
                    onClick={()=>{setSelectedOverlayId(null);setEditMode(v=>v==='native-images'?'native':'native-images');}}>
                    {editMode==='native-images'?'Görsel seçimi ✓':'Görsellere öncelik ver'}
                  </Button>}
                  <span className="editor-primary-hint">Seçili nesneyi taşıyın • Font ve görünüm sağda</span>
                </div>
                <Group gap={5} wrap="nowrap" className="editor-toolgroup">
                  <Tooltip label="Sola döndür"><ActionIcon color="gray" variant="subtle" size="lg" onClick={() => rotate(-90)} disabled={busy}><IconRotate2 size={20}/></ActionIcon></Tooltip>
                  <Tooltip label="Sağa döndür"><ActionIcon color="gray" variant="subtle" size="lg" onClick={() => rotate(90)} disabled={busy}><IconRotateClockwise size={20}/></ActionIcon></Tooltip>
                  <Divider orientation="vertical" />
                  <Tooltip label="Sayfayı kırp"><ActionIcon color="gray" variant="subtle" size="lg" onClick={() => setCropOpened(true)} disabled={busy}><IconCrop size={20}/></ActionIcon></Tooltip>
                  <Tooltip label="Boş sayfa ekle"><ActionIcon color="gray" variant="subtle" size="lg" onClick={insertBlank} disabled={busy}><IconFilePlus size={20}/></ActionIcon></Tooltip>
                  <Tooltip label="Sayfayı çoğalt"><ActionIcon color="gray" variant="subtle" size="lg" onClick={duplicatePage} disabled={busy}><IconCopy size={20}/></ActionIcon></Tooltip>
                  <Tooltip label="Sayfayı sil"><ActionIcon color="red" variant="subtle" size="lg" onClick={deleteSelected} disabled={busy}><IconTrash size={20}/></ActionIcon></Tooltip>
                </Group>
                <Group gap={7} wrap="nowrap" className="zoom-group"><ActionIcon color="gray" variant="subtle" onClick={() => chooseZoom(-1)} disabled={zoom === zoomOptions[0]}><IconZoomOut size={19}/></ActionIcon><span>{Math.round(zoom * 100)}%</span><ActionIcon color="gray" variant="subtle" onClick={() => chooseZoom(1)} disabled={zoom === zoomOptions.at(-1)}><IconZoomIn size={19}/></ActionIcon></Group>
              </div>
              {editMode && <Alert data-testid="unified-edit-status" color={native.status==='error'?'red':'teal'} variant="light" p="sm" m="xs">
                <b>PDF içeriğini düzenle:</b> {native.status==='loading'?'Yerel PDF motoru hazırlanıyor...':native.status==='error'?'PDF motoru kullanılamıyor.':native.status==='ready'?'Belgedeki gerçek metin veya görsele tıklayıp sağdan düzenle.':'Bağlantı kuruluyor...'}
                {native.error && <Text size="xs" c="red">{native.error}</Text>}
                {native.objects?.warning && <Text size="xs" c="orange">{native.objects.warning}</Text>}
                {' '}<Button size="compact-xs" variant="subtle" onClick={()=>{setEditMode(null);native.choose(null);}}>Bitir</Button>
              </Alert>}
              {selectedPage && !activeOverlay && !editMode && <div className="editor-ready-hint"><IconInfoCircle size={16}/> <span><strong>Yeni düzenleyici:</strong> Üstteki <b>Metin ekle</b> veya <b>Görsel ekle</b> düğmesini kullanın. Nesneye tıklayınca sağda özellikleri açılır.</span></div>}
              <div className="preview-scroll">
                <div className="page-preview-card" key={selectedPage?.id}>
                  <PdfCanvas descriptor={selectedPage} sources={sourcesRef.current} cache={previewCacheRef.current} zoom={zoom}
                    selectedOverlayId={selectedOverlayId}
                    textDraft={textDraft}
                    onSelectOverlay={(id) => setSelectedOverlayId(id)}
                    onOverlayChange={updateOverlay}
                    onOverlayDoubleClick={(id) => setSelectedOverlayId(id)}
                    editMode={editMode} unifiedNative={native} onNativeTextPick={pickExistingText} onNativeImagePick={pickExistingImage} />
                </div>
              </div>
              <div className="status-bar"><span><IconLock size={14}/> Belgeler tarayıcıda ve portal PDF motorunda işlenir</span><Group gap="xs" wrap="nowrap"><ActionIcon size="sm" color="gray" variant="subtle" disabled={selectedIndex < 1} onClick={() => goPage(-1)}><IconArrowLeft size={17}/></ActionIcon><strong>{selectedIndex + 1} / {pages.length}</strong><ActionIcon size="sm" color="gray" variant="subtle" disabled={selectedIndex === pages.length - 1} onClick={() => goPage(1)}><IconArrowRight size={17}/></ActionIcon></Group></div>
            </section>
            {editMode && native.selected ? <NativeObjectInspector engine={native} onClose={() => native.choose(null)}/>
              : activeOverlay
              ? <ObjectInspector key={activeOverlay.id} item={activeOverlay}
                  bounds={visualBounds(selectedPage)} onChange={updateOverlay}
                  onTextDraft={(id, text) => setTextDraft(id ? { id, text } : null)}
                  onReplaceImage={replaceOverlayImage} onCompositeMode={enableCompositeMode}
                  onClose={() => { setSelectedOverlayId(null); setTextDraft(null); }} onDelete={deleteOverlay} onDuplicate={duplicateOverlay} />
              : <aside className="object-inspector object-inspector-empty">
                  <div className="inspector-heading"><div><strong>Özellikler</strong><p>Seçili içerik bulunmuyor</p></div></div>
                  <div className="inspector-placeholder"><IconArrowsMove size={31}/><strong>Bir nesne seçin</strong><span>Metin veya görsel ekleyin; özgün PDF öğeleri için PDF Düzenle aracını açıp nesneye tıklayın. Hepsi bu çalışma alanında.</span></div>
                </aside>}
          </div>
        )}
      </div>

      <Modal opened={extractOpened} onClose={() => setExtractOpened(false)} title="Sayfa aralığını ayır" centered>
        <Stack gap="md"><Text size="sm" c="dimmed">Belirttiğiniz sayfalar özgün biçimleri korunarak yeni bir PDF dosyasına aktarılır.</Text><TextInput label="Sayfa numaraları" description={`1 ile ${pages.length} arasında. Örnek: 1-3, 5, 7-9`} placeholder="1-3, 5" value={extractRange} onChange={(event) => setExtractRange(event.currentTarget.value)} /><Group justify="end"><Button variant="default" onClick={() => setExtractOpened(false)}>Vazgeç</Button><Button onClick={exportRanges} loading={busy} leftSection={<IconDownload size={17}/>}>PDF'yi indir</Button></Group></Stack>
      </Modal>
      <Modal opened={cropOpened} onClose={() => setCropOpened(false)} title="Sayfayı kırp" centered>
        <Stack gap="md"><Alert color="blue" variant="light" icon={<IconInfoCircle size={17}/>}>Kırpma, PDF'nin görünür alanını değiştirir; gizlenen içerik dosyada kalabilir. Gizli bilgileri güvenli silmek için kullanılmamalıdır.</Alert><Text size="sm">Sayfa kenarlarından kırpılacak oran (%)</Text><SimpleGrid cols={2}><NumberInput min={0} max={95} label="Sol" value={margins.left} onChange={(value) => setMargins((v) => ({ ...v, left: Number(value) || 0 }))} /><NumberInput min={0} max={95} label="Sağ" value={margins.right} onChange={(value) => setMargins((v) => ({ ...v, right: Number(value) || 0 }))} /><NumberInput min={0} max={95} label="Üst" value={margins.top} onChange={(value) => setMargins((v) => ({ ...v, top: Number(value) || 0 }))} /><NumberInput min={0} max={95} label="Alt" value={margins.bottom} onChange={(value) => setMargins((v) => ({ ...v, bottom: Number(value) || 0 }))} /></SimpleGrid><Group justify="end"><Button variant="default" onClick={() => setCropOpened(false)}>İptal</Button><Button loading={busy} onClick={cropCurrent}>Kırpmayı uygula</Button></Group></Stack>
      </Modal>
    </div>
  );
}

export default function PdfStudioEditor({ onOpenAudit, isAdmin = false }) {
  return <LegacyApp onOpenAudit={onOpenAudit} isAdmin={isAdmin} />;
}
